import { useState } from "react";
import { useMutation, useQuery } from "convex/react";
import { AnimatePresence, motion } from "framer-motion";
import { api } from "@convex/_generated/api";
import { SUBJECT_COLORS } from "@convex/lib/colors";
import type { Doc, Id } from "@convex/_generated/dataModel";
import {
  cardClass,
  errorMessage,
  inputClass,
  primaryButtonClass,
  secondaryButtonClass,
} from "@/lib/ui";

type Draft = {
  name: string;
  shortCode: string;
  color: string;
  promptOverride: string;
};

function emptyDraft(index: number): Draft {
  return {
    name: "",
    shortCode: "",
    color: SUBJECT_COLORS[index % SUBJECT_COLORS.length],
    promptOverride: "",
  };
}

export default function SubjectsPanel() {
  const subjects = useQuery(api.subjects.list, { includeArchived: true });
  const [editing, setEditing] = useState<Id<"subjects"> | null>(null);
  const [creating, setCreating] = useState(false);

  if (subjects === undefined) {
    return <p className="text-sm text-neutral-500">Loading…</p>;
  }

  const active = subjects.filter((subject) => !subject.archived);
  const archived = subjects.filter((subject) => subject.archived);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2.5">
        <p className="text-sm text-neutral-500">
          A subject carries the colour used across the calendar, and can override
          the summary prompt.
        </p>
        {!creating && (
          <motion.button
            type="button"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.97 }}
            className={`${secondaryButtonClass} self-start sm:self-auto shrink-0`}
            onClick={() => setCreating(true)}
          >
            New subject
          </motion.button>
        )}
      </div>

      <AnimatePresence>
        {creating && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden"
          >
            <SubjectForm
              draft={emptyDraft(subjects.length)}
              onDone={() => setCreating(false)}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {subjects.length === 0 && !creating && (
        <p className={`${cardClass} text-sm text-neutral-500`}>
          No subjects yet. Add one to start building the timetable.
        </p>
      )}

      <div className="flex flex-col gap-2">
        <AnimatePresence initial={false}>
          {active.map((subject) =>
            editing === subject._id ? (
              <motion.div
                key={subject._id}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <SubjectForm
                  subject={subject}
                  draft={{
                    name: subject.name,
                    shortCode: subject.shortCode ?? "",
                    color: subject.color,
                    promptOverride: subject.promptOverride ?? "",
                  }}
                  onDone={() => setEditing(null)}
                />
              </motion.div>
            ) : (
              <motion.div
                key={subject._id}
                layout
                initial={{ opacity: 0, y: 3 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.98 }}
              >
                <SubjectRow
                  subject={subject}
                  onEdit={() => setEditing(subject._id)}
                />
              </motion.div>
            ),
          )}
        </AnimatePresence>
      </div>

      {archived.length > 0 && (
        <div className="flex flex-col gap-2">
          <h3 className="text-xs font-medium uppercase tracking-wide text-neutral-500">
            Archived
          </h3>
          <AnimatePresence initial={false}>
            {archived.map((subject) => (
              <motion.div
                key={subject._id}
                layout
                initial={{ opacity: 0, y: 3 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.98 }}
              >
                <SubjectRow
                  subject={subject}
                  onEdit={() => setEditing(subject._id)}
                />
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
}

function SubjectRow({
  subject,
  onEdit,
}: {
  subject: Doc<"subjects">;
  onEdit: () => void;
}) {
  const update = useMutation(api.subjects.update);
  const remove = useMutation(api.subjects.remove);
  const [error, setError] = useState<string | null>(null);

  async function run(action: () => Promise<unknown>, fallback: string) {
    setError(null);
    try {
      await action();
    } catch (caught) {
      setError(errorMessage(caught, fallback));
    }
  }

  return (
    <div className={`${cardClass} flex flex-wrap items-center gap-3`}>
      <div className="flex items-center gap-2.5 min-w-0">
        <span
          aria-hidden
          className="h-4 w-4 shrink-0 rounded-full"
          style={{ backgroundColor: subject.color }}
        />
        <span className={subject.archived ? "text-neutral-500 truncate" : "font-medium truncate"}>
          {subject.name}
        </span>
        {subject.shortCode !== undefined && (
          <span className="rounded bg-neutral-100 px-1.5 py-0.5 text-xs text-neutral-600 dark:bg-neutral-800 dark:text-neutral-400 shrink-0">
            {subject.shortCode}
          </span>
        )}
      </div>
      {subject.promptOverride !== undefined && (
        <span className="text-xs text-neutral-500">custom prompt</span>
      )}

      <div className="w-full sm:w-auto sm:ml-auto flex items-center justify-end gap-2 pt-2 sm:pt-0 border-t sm:border-t-0 border-neutral-100 dark:border-neutral-800/80">
        <button type="button" className={secondaryButtonClass} onClick={onEdit}>
          Edit
        </button>
        <button
          type="button"
          className={secondaryButtonClass}
          onClick={() =>
            void run(
              () => update({ id: subject._id, archived: !subject.archived }),
              "Could not change the archived state.",
            )
          }
        >
          {subject.archived ? "Unarchive" : "Archive"}
        </button>
        <button
          type="button"
          className={secondaryButtonClass}
          onClick={() =>
            void run(
              () => remove({ id: subject._id }),
              "Could not delete the subject.",
            )
          }
        >
          Delete
        </button>
      </div>

      {error !== null && (
        <p className="w-full text-sm text-red-600 dark:text-red-400">{error}</p>
      )}
    </div>
  );
}

function SubjectForm({
  subject,
  draft,
  onDone,
}: {
  subject?: Doc<"subjects">;
  draft: Draft;
  onDone: () => void;
}) {
  const create = useMutation(api.subjects.create);
  const update = useMutation(api.subjects.update);
  const [form, setForm] = useState<Draft>(draft);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  function set<K extends keyof Draft>(key: K, value: Draft[K]) {
    setForm((current) => ({ ...current, [key]: value }));
  }

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setSaving(true);
    try {
      if (subject === undefined) {
        await create({
          name: form.name,
          shortCode: form.shortCode,
          color: form.color,
          promptOverride: form.promptOverride,
        });
      } else {
        await update({
          id: subject._id,
          name: form.name,
          shortCode: form.shortCode,
          color: form.color,
          promptOverride: form.promptOverride,
        });
      }
      onDone();
    } catch (caught) {
      setError(errorMessage(caught, "Could not save the subject."));
    } finally {
      setSaving(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className={`${cardClass} flex flex-col gap-3`}>
      <div className="flex flex-wrap gap-3">
        <label className="flex w-full sm:min-w-64 sm:flex-1 flex-col gap-1 text-sm">
          <span className="text-neutral-600 dark:text-neutral-400">Name</span>
          <input
            className={inputClass}
            value={form.name}
            onChange={(e) => set("name", e.target.value)}
            autoFocus
          />
        </label>
        <label className="flex w-full sm:w-40 flex-col gap-1 text-sm">
          <span className="text-neutral-600 dark:text-neutral-400">
            Short code
          </span>
          <input
            className={inputClass}
            value={form.shortCode}
            onChange={(e) => set("shortCode", e.target.value)}
            placeholder="optional"
          />
        </label>
      </div>

      <div className="flex flex-col gap-1 text-sm">
        <span className="text-neutral-600 dark:text-neutral-400">Colour</span>
        <div className="flex flex-wrap gap-2">
          {SUBJECT_COLORS.map((color) => (
            <motion.button
              key={color}
              type="button"
              whileHover={{ scale: 1.15 }}
              whileTap={{ scale: 0.92 }}
              aria-label={color}
              aria-pressed={form.color.toUpperCase() === color}
              onClick={() => set("color", color)}
              className={[
                "h-7 w-7 rounded-full border-2 transition-transform cursor-pointer",
                form.color.toUpperCase() === color
                  ? "border-neutral-900 scale-110 dark:border-neutral-100"
                  : "border-transparent",
              ].join(" ")}
              style={{ backgroundColor: color }}
            />
          ))}
          <input
            className={`${inputClass} w-32 font-mono text-xs uppercase`}
            value={form.color}
            onChange={(e) => set("color", e.target.value)}
          />
        </div>
      </div>

      <label className="flex flex-col gap-1 text-sm">
        <span className="text-neutral-600 dark:text-neutral-400">
          Summary prompt override - leave empty to use the one in Settings
        </span>
        <textarea
          className={`${inputClass} min-h-24 font-mono text-xs`}
          value={form.promptOverride}
          onChange={(e) => set("promptOverride", e.target.value)}
        />
      </label>

      {error !== null && (
        <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
      )}

      <div className="flex items-center gap-2">
        <button type="submit" className={primaryButtonClass} disabled={saving}>
          {subject === undefined ? "Add subject" : "Save"}
        </button>
        <button type="button" className={secondaryButtonClass} onClick={onDone}>
          Cancel
        </button>
      </div>
    </form>
  );
}
